import { ObjectSerializer } from "../Serialization/ObjectSerializer";
import { ObjSerUtils } from "../Serialization/ObjSerUtils";
import { ValueEncoder } from "../Utils/ValueEncoder";
import { LayoutCluster, ILayoutCluster } from "../Topology/LayoutCluster";
import { TopoNode } from "../Topology/TopoNode";
import { DeviceNode } from "../Topology/DeviceNode";
import { TopoMap } from "../Topology/TopoMap";
import { SpanningTree } from "../Topology/SpanningTree";
import { TopoNodePath } from "../Topology/TopoNodePath";
import { RadialLayout, RadialLayoutSettings } from "../Topology/RadialLayout";
import { HierarchicalLayout, HierarchicalLayoutSettings } from "../Topology/HierarchicalLayout";
import { LayoutSettings } from "../Topology/LayoutSettings";
import { List } from "../Utils/List";
import { Point } from "../Utils/Matrix";


export class RingLayoutSettings implements ObjectSerializer
{
	private static readonly DefaultMinRadius = 200;
	private static readonly DefaultMaxRadius = 3000;
	private static readonly DefaultMaxAngle = 360;

	constructor() {
		this.MinRadius = RingLayoutSettings.DefaultMinRadius;
		this.MaxRadius = RingLayoutSettings.DefaultMaxRadius;
		this.MaxAngle = RingLayoutSettings.DefaultMaxAngle;
		this.LayoutAngle = 0;
		this.MaximizeRoot = true;
	}

	public MinRadius: number;
	public MaxRadius: number;
	public MaxAngle: number;
	public MaximizeRoot: boolean;

	// Not persisted
	public LayoutAngle: number;

	public Clone(): RingLayoutSettings {
		let result = new RingLayoutSettings();

		result.MinRadius = this.MinRadius;
		result.MaxRadius = this.MaxRadius;
		result.MaxAngle = this.MaxAngle;
		result.MaximizeRoot = this.MaximizeRoot;
		result.LayoutAngle = this.LayoutAngle;

		return result;
	}

	public Write(obj: any, context: any): void {
		obj.ring_layout_settings = {};
		obj = obj.ring_layout_settings;

		obj.min_radius = ValueEncoder.EncodeInteger(this.MinRadius);
		obj.max_radius = ValueEncoder.EncodeInteger(this.MaxRadius);
		obj.max_angle = ValueEncoder.EncodeInteger(this.MaxAngle);
		obj.maximize_root = ValueEncoder.EncodeBoolean(this.MaximizeRoot);
	}

	public Read(obj: any, context: any): void {
		if (!obj.ring_layout_settings)
			throw new Error();
		obj = obj.ring_layout_settings;

		if (obj.min_radius !== undefined)
			this.MinRadius = ValueEncoder.DecodeInteger(obj.min_radius);
		if (obj.max_radius !== undefined)
			this.MaxRadius = ValueEncoder.DecodeInteger(obj.max_radius);
		if (obj.max_angle !== undefined)
			this.MaxAngle = ValueEncoder.DecodeInteger(obj.max_angle);
		if (obj.maximize_root !== undefined)
			this.MaximizeRoot = ValueEncoder.DecodeBoolean(obj.maximize_root);
	}

}

export class RingLayoutCluster extends LayoutCluster<RingLayoutCluster> {
	constructor(root: TopoNode) {
		super(root);
	}

	public Place(distance: number, angle: number): void {
		this.Root.X = 0;
		this.Root.Y = 0;

		const leftMost: Point = this.LeftMostLocation();

		if (leftMost.X < this.Root.X)
			distance += this.Root.X - leftMost.X;

		this.Offset(distance, 0);
		this.Rotate(angle);
	}
}

export class RingLayout {
	public static readonly DefaultTreeMargin = 100;

	private static readonly ThreeSixty = 360;
	private static readonly LayerMargin = 100;

	public MinRadius: number;
	public MaxRadius: number;
	public MaxAngle: number;
	public TreeMargin: number;
	public MaximizeRoot: boolean;
	public LayoutAngle: number;

	private _topoMap: TopoMap;
	private _radialLayout: RadialLayout;
	private _hierarchicalLayout: HierarchicalLayout;

	constructor(topoMap: TopoMap) {
		this._topoMap = topoMap;
		this.Settings = topoMap.LayoutSettings.RingSettings;
		this.TreeMargin = RingLayout.DefaultTreeMargin;

		this._radialLayout = new RadialLayout(topoMap);
		const radialSettings = new RadialLayoutSettings();
		radialSettings.MinRadius = topoMap.LayoutSettings.RingSettings.MinRadius;
		radialSettings.MaxRadius = topoMap.LayoutSettings.RingSettings.MaxRadius;
		radialSettings.MaxAngle = topoMap.LayoutSettings.RingSettings.MaxAngle;
		radialSettings.MaximizeRoot = topoMap.LayoutSettings.RingSettings.MaximizeRoot;
		this._radialLayout.Settings = radialSettings;
		this._radialLayout.TreeMargin = RingLayout.DefaultTreeMargin;

		this._hierarchicalLayout = new HierarchicalLayout(topoMap);
		this._hierarchicalLayout.Settings = new HierarchicalLayoutSettings();
		this._hierarchicalLayout.TreeMargin = RingLayout.DefaultTreeMargin;
	}

	public set Settings(value: RingLayoutSettings) {
		if (value != null) {
			this.MinRadius = value.MinRadius;
			this.MaxRadius = value.MaxRadius;
			this.MaxAngle = value.MaxAngle;
			this.MaximizeRoot = value.MaximizeRoot;
			this.LayoutAngle = value.LayoutAngle;

			if (this.MaxAngle <= 0) this.MaxAngle = 1;
			if (this.MaxRadius <= 0) this.MaxRadius = 1;
			if (this.MinRadius <= 0) this.MinRadius = 1;
		}
	}

	public LayoutSpanningTrees(spanningTrees: List<SpanningTree>): void {
		spanningTrees.ForEach((tree) => {
			const rootNode = tree.Root;
			if (rootNode == null)
				return;

			const nodePath = new TopoNodePath(this._topoMap);
			const centralRingPath = nodePath.GetCentralRing(rootNode);

			if (centralRingPath == null)
				return;

			const centerPoint = rootNode.Location;
			this.LayoutNodes_CentralRing(centralRingPath, this.MinRadius, centerPoint);

			const edgePathList = nodePath.GetEdgePaths(centralRingPath);

			const layoutSettings = new LayoutSettings();
			for (const edgePath of edgePathList) {
				const edgeRootNode = edgePath.RootNode;
				const edgeRootDevice = edgePath.RootDevice;

				const edgeMap = TopoNodePath.CreateTopoMap(edgePath);
				if (edgeMap == null)
					continue;

				const mapRootNode = edgeMap.FindNodeByDevice(edgeRootDevice);
				if (mapRootNode == null)
					continue;

				mapRootNode.Location = edgeRootNode.Location;
				mapRootNode.RingLayoutAngle = edgeRootNode.RingLayoutAngle;

				layoutSettings.RootNodes.Clear();
				layoutSettings.RootNodes.Add(mapRootNode);

				edgeMap.SetAsRoot(mapRootNode);
				edgeMap.LayoutChildren(mapRootNode, layoutSettings);

				edgeMap.VisibleNodes.ForEach((tempNode) => {
					const deviceNode = tempNode instanceof DeviceNode ? tempNode : null;
					if (deviceNode == null) return;
					if (deviceNode.Device == null) return;
					if (deviceNode.Device === edgeRootDevice) return;

					const pathNode = edgePath.FindByDevice(deviceNode.Device);
					if (pathNode != null)
						pathNode.Location = tempNode.Location;
				});
			}
		});
	}

	public LayoutChildren(node: TopoNode): RingLayoutCluster {
		let cluster: RingLayoutCluster = null;

		const tree = this._topoMap.OwningTree(node);
		if (tree != null) {
			const nodeLocation = node.Location;

			this._topoMap.DescendantNodes(node).ForEach((d) => {
				d.Location = new Point(0, 0);
			});

			cluster = this.LayoutNodes(node, tree);
			node.Offset(-nodeLocation.X, -nodeLocation.Y);

			cluster.Rotate(-this.LayoutAngle);
			cluster.Offset(nodeLocation.X, nodeLocation.Y);
		}

		return cluster;
	}

	public LayoutNodes(root: TopoNode, tree: SpanningTree): RingLayoutCluster {
		const currentCluster = new RingLayoutCluster(root);

		tree.ChildNodes(root).ForEach((child) => {
			currentCluster.AddCluster(this.LayoutNodes(child, tree));
		});

		const clusters = currentCluster.Clusters;
		if (clusters.Count > 0) {
			const requiredArcLength = this._requiredArcLength(clusters);
			let requiredAngle = this._requiredAngle(requiredArcLength);

			if (root === tree.Root && this.MaximizeRoot)
				requiredAngle = RingLayout.ThreeSixty;

			const requiredRadius = this._requiredRadius(requiredArcLength, requiredAngle);

			if (requiredRadius > this.MaxRadius) {
				const singles = new List<RingLayoutCluster>();
				const multiples = new List<RingLayoutCluster>();

				clusters.ForEach((cluster) => {
					if (cluster.Clusters.Count === 0)
						singles.Add(cluster);
					else
						multiples.Add(cluster);
				});

				const singlesArcLength = this._requiredArcLength(singles);
				const multiplesArcLength = this._requiredArcLength(multiples);
				const singlesAngle = this._requiredAngle(singlesArcLength);
				const multiplesAngle = this._requiredAngle(multiplesArcLength);

				let angle = Math.max(multiplesAngle, singlesAngle);
				if (root === tree.Root)
					angle = RingLayout.ThreeSixty;

				let radius = this.MinRadius;

				if (singles.Count > 0) {
					let allowLayering = false;
					radius = this._requiredRadius(singlesArcLength, angle);
					if (radius > this.MaxRadius) {
						radius = this.MaxRadius;
						allowLayering = true;
					}
					radius = this._layoutNodesAndClusters(root, tree, singles, singlesArcLength, angle, radius, allowLayering);
					radius += RingLayout.LayerMargin;
				}

				if (multiples.Count > 0) {
					this._orderClusters(multiples);
					this._layoutNodesAndClusters(root, tree, multiples, multiplesArcLength, angle, radius);
				}
			} else {
				this._orderClusters(clusters);
				this._layoutNodesAndClusters(root, tree, clusters, requiredArcLength, requiredAngle, this.MinRadius);
			}
		}

		currentCluster.CalculateSize();
		return currentCluster;
	}

	public LayoutNodes_CentralRing(ringPath: TopoNodePath, radius: number, center: Point): void {
		if (ringPath == null || ringPath.Count <= 1)
			return;

		const baseNode = ringPath.RootNode;
		const firstNode = ringPath.FirstNode;
		const lastNode = ringPath.LastNode;
		if (baseNode == null || firstNode == null || lastNode == null)
			return;

		const slice = RingLayout.ThreeSixty / ringPath.Count;
		let i = 0;

		for (const node of ringPath.Nodes) {
			const angle = slice * i++;
			const angleRadians = this._degreesToRadians(angle);
			node.Location = new Point(
				Math.round(center.X + radius * Math.cos(angleRadians)),
				Math.round(center.Y + radius * Math.sin(angleRadians))
			);
			node.RingLayoutAngle = Math.round(angle);
		}
	}

	public LayoutNodes_Ring(ringPath: TopoNodePath, radius: number, center: Point): void {
		if (ringPath == null || ringPath.Count <= 1)
			return;

		const baseNode = ringPath.RootNode;
		const firstNode = ringPath.FirstNode;
		const lastNode = ringPath.LastNode;
		if (baseNode == null || firstNode == null || lastNode == null)
			return;

		const startAngle = this._angle(firstNode, center);
		const startDegrees = this._radiansToDegrees(startAngle);
		const endAngle = this._angle(lastNode, center);
		const endDegrees = this._radiansToDegrees(endAngle);
		const totalDegrees = endDegrees - startDegrees;
		const slice = totalDegrees / (ringPath.Count - 3);

		let i = 1;
		const exclude = new Set<TopoNode>([baseNode, firstNode, lastNode]);

		for (const node of ringPath.Nodes) {
			if (exclude.has(node))
				continue;
			const angle = startDegrees + (slice * i++);
			const angleRadians = this._degreesToRadians(angle);
			node.Location = new Point(
				Math.round(center.X + radius * Math.cos(angleRadians)),
				Math.round(center.Y + radius * Math.sin(angleRadians))
			);
		}
	}

	public LayoutNodes_Ring_Open(ringPath: TopoNodePath, radius: number, center: Point): void {
		if (ringPath == null || ringPath.Count <= 1)
			return;

		const baseNode = ringPath.RootNode;
		const firstNode = ringPath.FirstNode;
		if (baseNode == null || firstNode == null)
			return;

		const startAngle = this._angle(firstNode, center);
		const startDegrees = this._radiansToDegrees(startAngle);
		const totalDegrees = 360;
		const slice = totalDegrees / (ringPath.Count - 1);

		let i = 1;
		const exclude = new Set<TopoNode>([baseNode, firstNode]);

		for (const node of ringPath.Nodes) {
			if (exclude.has(node))
				continue;
			const angle = startDegrees + (slice * i++);
			const angleRadians = this._degreesToRadians(angle);
			node.Location = new Point(
				Math.round(center.X + radius * Math.cos(angleRadians)),
				Math.round(center.Y + radius * Math.sin(angleRadians))
			);
		}
	}

	// ---- Private helpers ----

	private _layoutNodesAndClusters(root: TopoNode, tree: SpanningTree, clusters: List<RingLayoutCluster>,
		arcLength: number, sectorAngle: number, minRadius: number, allowLayering: boolean = false): number {

		const requiredAngle = this._requiredAngle(arcLength, minRadius, sectorAngle, !allowLayering);

		const startDegree = RingLayout.ThreeSixty - sectorAngle / 2;

		let regionAngleMargin = 0;
		if (requiredAngle < sectorAngle)
			regionAngleMargin = (sectorAngle - requiredAngle) / clusters.Count;

		let currentDegree = (startDegree + regionAngleMargin / 2) % RingLayout.ThreeSixty;
		let angleCovered = 0;

		for (let i = 0; i < clusters.Count; i++) {
			const node = clusters.Get(i);

			const regionAngle = requiredAngle * (node.Height / arcLength);
			const regionStart = currentDegree;

			let nodeAngle = (regionStart + regionAngle * (Math.abs(node.Top) / (node.Height === 0 ? 1 : node.Height))) % RingLayout.ThreeSixty;

			if (nodeAngle < startDegree && nodeAngle > (startDegree + sectorAngle) % RingLayout.ThreeSixty)
				nodeAngle = (startDegree + (nodeAngle + RingLayout.ThreeSixty - (startDegree + sectorAngle))) % RingLayout.ThreeSixty;

			let radius = this._requiredRadius(node.Height, regionAngle + regionAngleMargin, minRadius);

			if (allowLayering)
				radius += RingLayout.LayerMargin * Math.floor(angleCovered / sectorAngle);

			node.Place(radius, nodeAngle);

			angleCovered += regionAngle + regionAngleMargin;
			currentDegree = (currentDegree + regionAngle + regionAngleMargin) % RingLayout.ThreeSixty;
			if (currentDegree < startDegree && currentDegree > (startDegree + sectorAngle) % RingLayout.ThreeSixty)
				currentDegree = (startDegree + sectorAngle - currentDegree) % RingLayout.ThreeSixty;
		}

		return this._requiredRadius(arcLength, requiredAngle, minRadius);
	}

	private _orderClusters(clusters: List<RingLayoutCluster>): void {
		clusters.Values.sort((c1, c2) => c1.Width === c2.Width ? 0 : c1.Width > c2.Width ? 1 : -1);

		for (let i = 1; i < clusters.Count / 2; i++) {
			const move = clusters.Values[i];
			clusters.RemoveAt(i);
			clusters.Values.splice(clusters.Count + 1 - i, 0, move);
		}
	}

	private _requiredArcLength(clusters: List<RingLayoutCluster>): number {
		let arcLength = 0;
		clusters.ForEach((cluster) => { arcLength += cluster.Height; });
		return arcLength;
	}

	private _requiredRadius(requiredArcLength: number, angle: number, minRadius: number = null): number {
		if (minRadius == null) minRadius = this.MinRadius;
		let result = (requiredArcLength * RingLayout.ThreeSixty) / (angle * 2 * Math.PI);
		if (result < minRadius) result = minRadius;
		return result;
	}

	private _requiredAngle(requiredArcLength: number, radius: number = null, maxAngle: number = null, restrictAngle: boolean = true): number {
		if (radius == null) radius = this.MinRadius;
		if (maxAngle == null) maxAngle = this.MaxAngle;
		let angle = (requiredArcLength * RingLayout.ThreeSixty) / (2 * Math.PI * radius);
		if (restrictAngle && angle > maxAngle)
			angle = maxAngle;
		return angle;
	}

	private _radiansToDegrees(radians: number): number {
		let degrees = radians * (180 / Math.PI);
		if (degrees < 0.0)
			degrees = 360 + degrees;
		return degrees;
	}

	private _degreesToRadians(degrees: number): number {
		return (Math.PI / 180) * degrees;
	}

	private _angle(node: TopoNode, centerPoint: Point): number {
		if (node == null) return 0;
		return Math.atan2(node.Y - centerPoint.Y, node.X - centerPoint.X);
	}
}

